/**
 * iWishBag AI Chat Widget
 * Hybrid Architecture - Connected to Cloudflare D1 + Supabase
 * ENHANCED with Emergency Performance Safeguards
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { OptimizedIcon } from '@/components/ui/OptimizedIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { supabase } from '@/integrations/supabase/client';
import { FileUpload } from '@/components/chat/FileUpload';
import { FileMessage } from '@/components/chat/FileMessage';
// import { useRenderTracking, EmergencyBailoutUI } from '@/utils/performanceMonitor.tsx';

interface ChatMessage {
  id: string;
  senderType: 'customer' | 'bot' | 'agent' | 'system';
  messageType: 'text' | 'image' | 'file' | 'quick_reply' | 'card';
  content: {
    text?: string;
    imageUrl?: string;
    fileUrl?: string;
    quickReplies?: string[];
  };
  attachments?: ChatAttachment[]; // File attachments
  reactions?: ChatReaction[]; // Emoji reactions
  senderName?: string;
  createdAt: number;
  aiIntent?: string;
  aiConfidence?: number;
}

interface ChatReaction {
  emoji: string;
  count: number;
  userReacted?: boolean;
}

interface ChatAttachment {
  id: string;
  originalFilename: string;
  storedFilename: string;
  fileSize: number;
  mimeType: string;
  fileExtension: string;
  fileCategory: 'image' | 'document' | 'audio' | 'video';
  isImage: boolean;
  r2Key: string;
  r2Url?: string;
  workerUrl?: string; // Backup worker endpoint for file access
  uploadStatus: 'uploading' | 'uploaded' | 'failed' | 'processing';
  createdAt: number;
}

interface Conversation {
  id: string;
  status: string;
  priority: string;
  language: string;
  assignedAgentId?: string;
  agentName?: string;
}

const CHAT_API_URL = 'https://iwishbag-chatbot.rnkbohra.workers.dev'; // Always use production for R2 uploads

// JHAKKAS FIX: ChatWidget implementation with emergency safeguards
const ChatWidgetImpl: React.FC = () => {
  const { user } = useAuth();
  
  // EMERGENCY PERFORMANCE MONITORING: Disabled per user request
  // const { shouldBailout, renderCount, resetMetrics } = useRenderTracking('ChatWidget');
  
  // SAFETY CHECK: Never render on admin pages (belt and suspenders approach)
  if (window.location.pathname.startsWith('/admin')) {
    console.log('[admin-user-chat] 🛑 ChatWidget: BLOCKED on admin page:', window.location.pathname);
    return null;
  }

  // EMERGENCY BAILOUT: Disabled per user request
  // if (shouldBailout) {
  //   console.error('[admin-user-chat] 🚨 ChatWidget EMERGENCY BAILOUT triggered!');
  //   return (
  //     <EmergencyBailoutUI 
  //       componentName="ChatWidget" 
  //       renderCount={renderCount}
  //       onReset={() => {
  //         resetMetrics();
  //         window.location.reload();
  //       }}
  //     />
  //   );
  // }
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch user profile when user changes
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id || user?.is_anonymous) {
        setUserProfile(null);
        return;
      }

      // Skip Supabase profile fetching in dev mode for mock users
      const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';
      const isDevUser = user.id.startsWith('dev-');

      if (isDevMode && isDevUser) {
        // Create mock profile for dev users
        setUserProfile({
          full_name: user.user_metadata?.name || 'Dev User',
          email: user.email || 'dev@iwishbag.com',
          phone: user.phone || null,
          country: 'US'
        });
        logger.debug('🚨 DEV MODE: Using mock profile for dev user', { userId: user.id });
        return;
      }

      try {
        // Use real profile service instead of Supabase
        const { realProfileService } = await import('@/services/RealProfileService');
        const profile = await realProfileService.getProfile(user.id);

        if (profile) {
          setUserProfile({
            full_name: profile.full_name,
            phone: profile.phone,
            email: profile.email,
            country: profile.country
          });

        } else {

        }
      } catch (error) {
        logger.error('Failed to fetch user profile:', error);
      }
    };

    fetchUserProfile();
  }, [user?.id, user?.is_anonymous]);

  // Load conversation history
  useEffect(() => {
    if (isOpen && conversation?.id) {
      loadConversationHistory();
    }
  }, [isOpen, conversation?.id]);

  // Try to restore previous conversation on mount
  useEffect(() => {
    const savedSessionId = localStorage.getItem('chat_session_id');
    
    if (savedSessionId && !conversation?.id && user?.id) {
      
      setConversation({ id: savedSessionId } as Conversation);
    }
  }, [user?.id]); // Run when user loads

  const loadConversationHistory = async () => {
    
    if (!conversation?.id) {
      
      return;
    }

    try {
      const historyUrl = `${CHAT_API_URL}/api/chat/history?conversationId=${conversation.id}`;
      
      const response = await fetch(historyUrl);
      const data = await response.json();
      
      console.log('[CHAT-BOT-DEBUG] History response:', {
        success: data.success,
        messageCount: data.messages?.length || 0,
        firstMessage: data.messages?.[0]?.content ? 
          (typeof data.messages[0].content === 'string' ? 
            JSON.parse(data.messages[0].content).text?.substring(0, 50) + '...' : 
            data.messages[0].content.text?.substring(0, 50) + '...'
          ) : 'none'
      });
      
      if (data.success && data.messages?.length > 0) {
        const formattedMessages = data.messages.map((msg: any) => ({
          id: msg.id,
          senderType: msg.sender_type,
          messageType: msg.message_type,
          content: typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content,
          senderName: msg.sender_name,
          createdAt: msg.created_at,
          aiIntent: msg.ai_intent,
          aiConfidence: msg.ai_confidence
        }));
        setMessages(formattedMessages);
        
      } else {
        
      }
    } catch (error) {
      logger.error('Failed to load conversation history:', error);
      
    }
  };

  const sendMessage = async (text: string, quickReply = false) => {
    if (!text.trim() || isLoading) return;

    const tempMessage: ChatMessage = {
      id: 'temp-' + Date.now(),
      senderType: 'customer',
      messageType: 'text',
      content: { text },
      createdAt: Date.now()
    };

    setMessages(prev => [...prev, tempMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      const payload = {
        message: text,
        conversationId: conversation?.id,
        customerId: user?.id || 'anonymous',
        customerName: userProfile?.full_name || user?.user_metadata?.full_name || null,
        customerEmail: userProfile?.email || user?.email || null,
        customerPhone: userProfile?.phone || user?.user_metadata?.phone || null,
        sessionId: localStorage.getItem('chat_session_id') || undefined,
        platform: 'website'
      };

      
      // [cust-info] DEBUG: Frontend customer data analysis
      
      
      console.log('[CHAT-BOT-DEBUG] Current conversation state:', {
        conversationId: conversation?.id,
        sessionIdFromStorage: localStorage.getItem('chat_session_id'),
        payloadConversationId: payload.conversationId
      });

      const response = await fetch(`${CHAT_API_URL}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        // Message sent successfully

        // Update conversation ID if new
        if (data.conversationId && !conversation?.id) {
          setConversation(prev => ({ ...prev, id: data.conversationId } as Conversation));
          
        }

        // Remove temp message and add real messages
        setMessages(prev => {
          const withoutTemp = prev.filter(msg => msg.id !== tempMessage.id);
          return [
            ...withoutTemp,
            {
              id: tempMessage.id.replace('temp-', ''),
              senderType: 'customer',
              messageType: 'text',
              content: { text },
              createdAt: tempMessage.createdAt
            },
            {
              id: data.message.id,
              senderType: data.message.senderType,
              messageType: 'text',
              content: data.message.content,
              createdAt: data.message.createdAt,
              aiIntent: data.intent,
              aiConfidence: data.confidence
            }
          ];
        });

        // Store session ID
        if (!localStorage.getItem('chat_session_id')) {
          localStorage.setItem('chat_session_id', data.conversationId);
          
        } else {
          
        }
      }
    } catch (error) {
      logger.error('Failed to send message:', error);
      // Remove temp message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleQuickReply = (reply: string) => {
    sendMessage(reply, true);
  };

  const handleEmojiReaction = (messageId: string, emoji: string) => {
    setMessages(prev => prev.map(msg => {
      if (msg.id !== messageId) return msg;
      
      const reactions = msg.reactions || [];
      const existingReaction = reactions.find(r => r.emoji === emoji);
      
      if (existingReaction) {
        // Toggle reaction
        if (existingReaction.userReacted) {
          existingReaction.count--;
          existingReaction.userReacted = false;
          if (existingReaction.count === 0) {
            return {
              ...msg,
              reactions: reactions.filter(r => r.emoji !== emoji)
            };
          }
        } else {
          existingReaction.count++;
          existingReaction.userReacted = true;
        }
      } else {
        // Add new reaction
        reactions.push({
          emoji,
          count: 1,
          userReacted: true
        });
      }
      
      return {
        ...msg,
        reactions: [...reactions]
      };
    }));
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getWelcomeMessage = (): ChatMessage => {
    const language = user?.profile?.preferred_language || 'en';
    const welcomeMessages = {
      en: "Hi! I'm iWishBag AI assistant. I can help you with order tracking, new quotes, shipping information, and general support. What would you like to do today?",
      hi: "नमस्ते! मैं iWishBag का AI असिस्टेंट हूं। मैं ऑर्डर ट्रैकिंग, नए कोट्स, शिपिंग जानकारी और सामान्य सपोर्ट में आपकी मदद कर सकता हूं। आज आप क्या करना चाहेंगे?",
      ne: "नमस्कार! म iWishBag को AI सहायक हुँ। म अर्डर ट्र्याकिङ, नयाँ कोटहरू, ढुवानी जानकारी र सामान्य सहयोगमा तपाईंलाई मद्दत गर्न सक्छु। आज तपाईं के गर्न चाहनुहुन्छ?"
    };

    const quickReplies = {
      en: ["Track Order", "Get Quote", "Shipping Info", "Talk to Human", "Help Center"],
      hi: ["ऑर्डर ट्रैक करें", "कोट लें", "शिपिंग जानकारी", "इंसान से बात करें", "सहायता केंद्र"],
      ne: ["अर्डर ट्र्याक गर्नुहोस्", "कोट लिनुहोस्", "ढुवानी जानकारी", "मान्छेसँग कुरा गर्नुहोस्", "सहयोग केन्द्र"]
    };

    return {
      id: 'welcome-' + Date.now(),
      senderType: 'bot',
      messageType: 'text',
      content: {
        text: welcomeMessages[language] || welcomeMessages.en,
        quickReplies: quickReplies[language] || quickReplies.en
      },
      createdAt: Date.now()
    };
  };

  const openChat = () => {
    setIsOpen(true);
    setUnreadCount(0);
    
    // Debug session management
    const savedSessionId = localStorage.getItem('chat_session_id');
    
    // Add welcome message if no messages
    if (messages.length === 0) {
      setMessages([getWelcomeMessage()]);
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Handle file upload completion
  const handleFileUploaded = (attachment: ChatAttachment) => {
    console.log('[FILE-UPLOAD] 📎 File uploaded successfully:', attachment);
    
    // Create file message
    const fileMessage: ChatMessage = {
      id: 'file-' + Date.now(),
      senderType: 'customer',
      messageType: 'file',
      content: { text: `📎 ${attachment.originalFilename}` },
      attachments: [attachment],
      senderName: user?.email || 'You',
      createdAt: Date.now()
    };
    
    // Add to messages immediately for instant feedback
    setMessages(prev => [...prev, fileMessage]);
    
    // Hide file upload panel
    setShowFileUpload(false);
    
    // TODO: Send to backend for processing by agent/bot
    // For now, just add success message
    setTimeout(() => {
      const confirmMessage: ChatMessage = {
        id: 'confirm-' + Date.now(),
        senderType: 'bot',
        messageType: 'text',
        content: { text: `✅ File "${attachment.originalFilename}" uploaded successfully! An agent will review it shortly.` },
        createdAt: Date.now()
      };
      setMessages(prev => [...prev, confirmMessage]);
    }, 1000);
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={openChat}
          className="relative w-14 h-14 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
          size="lg"
        >
          <OptimizedIcon name="MessageCircle" className="w-6 h-6 text-white" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center p-0">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
        
        {/* Floating tooltip */}
        <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-lg p-3 text-sm text-gray-700 max-w-xs opacity-0 pointer-events-none transition-opacity duration-200 group-hover:opacity-100">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Need help? Chat with our AI assistant!</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-6 md:right-6 z-50 sm:w-80 md:w-96">
      <Card className={`w-full bg-white shadow-2xl border-0 transition-all duration-300 ${
        isMinimized 
          ? 'h-14 sm:h-16' 
          : 'h-[500px] max-h-[75vh]'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <OptimizedIcon name="Bot" className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">iWishBag Assistant</h3>
              {conversation?.agentName ? (
                <p className="text-xs text-white/80">Connected to {conversation.agentName}</p>
              ) : (
                <div className="flex items-center gap-1 text-xs text-white/80">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span>AI Assistant Online</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={toggleMinimize}
              variant="ghost"
              size="sm"
              className="w-8 h-8 p-0 text-white hover:bg-white/20"
            >
              <OptimizedIcon 
                name={isMinimized ? "ChevronUp" : "ChevronDown"} 
                className="w-4 h-4" 
              />
            </Button>
            <Button
              onClick={() => setIsOpen(false)}
              variant="ghost"
              size="sm"
              className="w-8 h-8 p-0 text-white hover:bg-white/20"
            >
              <OptimizedIcon name="X" className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <CardContent className={`p-0 overflow-y-auto bg-gray-50 ${
              showFileUpload ? 'h-48' : 'h-80'
            }`}>
              <div className="p-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.senderType === 'customer' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`group max-w-[80%] rounded-lg p-3 text-sm ${
                        message.senderType === 'customer'
                          ? 'bg-teal-500 text-white'
                          : message.senderType === 'bot'
                          ? 'bg-white border text-gray-800'
                          : 'bg-blue-50 border border-blue-200 text-blue-800'
                      }`}
                    >
                      {/* Text Content */}
                      {message.content.text && (
                        <p className="whitespace-pre-wrap">{message.content.text}</p>
                      )}
                      
                      {/* File Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2">
                          {message.attachments.map((attachment) => (
                            <FileMessage 
                              key={attachment.id}
                              attachment={attachment}
                              isOwn={message.senderType === 'customer'}
                            />
                          ))}
                        </div>
                      )}
                      
                      {/* Quick Replies */}
                      {message.content.quickReplies && (
                        <div className="mt-2 space-y-1">
                          {message.content.quickReplies.map((reply, index) => (
                            <button
                              key={index}
                              onClick={() => handleQuickReply(reply)}
                              className="block w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded border transition-colors"
                            >
                              {reply}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Emoji Reactions */}
                      {message.reactions && message.reactions.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {message.reactions.map((reaction, index) => (
                            <button
                              key={index}
                              onClick={() => handleEmojiReaction(message.id, reaction.emoji)}
                              className={`px-2 py-1 rounded-full text-xs border transition-colors ${
                                reaction.userReacted
                                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                                  : 'bg-gray-100 border-gray-200 hover:bg-gray-200'
                              }`}
                            >
                              {reaction.emoji} {reaction.count}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Add Reaction Button (only for bot/agent messages) */}
                      {message.senderType !== 'customer' && (
                        <div className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex gap-1">
                            {['👍', '👎', '❤️', '😊', '🤔'].map((emoji) => (
                              <button
                                key={emoji}
                                onClick={() => handleEmojiReaction(message.id, emoji)}
                                className="p-1 text-xs hover:bg-gray-200 rounded transition-colors"
                                title={`React with ${emoji}`}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className={`text-xs mt-1 ${
                        message.senderType === 'customer' ? 'text-teal-100' : 'text-gray-500'
                      }`}>
                        {formatTime(message.createdAt)}
                        {message.aiIntent && (
                          <span className="ml-2 opacity-70">
                            • {message.aiIntent}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Typing indicator */}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white border rounded-lg p-3 text-sm">
                      <div className="flex items-center gap-1">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                        <span className="text-gray-500 ml-2">AI is thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </CardContent>

            {/* File Upload Panel */}
            {showFileUpload && (
              <div className="border-t bg-white">
                {/* File Upload Header */}
                <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <OptimizedIcon name="Paperclip" className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">Attach File</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFileUpload(false)}
                    className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                  >
                    <OptimizedIcon name="X" className="w-3 h-3" />
                  </Button>
                </div>
                
                {/* File Upload Component */}
                <div className="p-3">
                  <FileUpload
                    onFileUploaded={handleFileUploaded}
                    conversationId={conversation?.id || 'temp-conversation'}
                    uploadedBy={user?.id || 'anonymous'}
                    compact={true}
                  />
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t bg-white rounded-b-lg">
              <div className="flex items-center gap-2">
                {/* File Attachment Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFileUpload(!showFileUpload)}
                  className="h-9 w-9 p-0 text-gray-500 hover:text-teal-600"
                  title="Attach file"
                >
                  <OptimizedIcon name="Paperclip" className="w-4 h-4" />
                </Button>
                
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type your message..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(inputMessage);
                    }
                  }}
                  disabled={isLoading}
                  className="flex-1 border-gray-200 focus:border-teal-500"
                />
                
                <Button
                  onClick={() => sendMessage(inputMessage)}
                  disabled={!inputMessage.trim() || isLoading}
                  size="sm"
                  className="bg-teal-500 hover:bg-teal-600 text-white px-3"
                >
                  {isLoading ? (
                    <OptimizedIcon name="Loader2" className="w-4 h-4 animate-spin" />
                  ) : (
                    <OptimizedIcon name="Send" className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                <span>Powered by iWishBag AI</span>
                {conversation?.status === 'escalated' && (
                  <Badge variant="outline" className="text-xs">
                    <OptimizedIcon name="User" className="w-3 h-3 mr-1" />
                    Agent will join soon
                  </Badge>
                )}
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

// JHAKKAS FIX: Enhanced React.memo with custom comparison for ChatWidget
const chatWidgetMemoComparison = (prevProps: any, nextProps: any) => {
  
  // Since ChatWidget has no props, we can always block re-renders from parent
  // All state changes are handled internally via hooks and context
  return true; // Always return true = never re-render due to parent changes
};

// Export the memoized ChatWidget with emergency safeguards
export const ChatWidget = React.memo(ChatWidgetImpl, chatWidgetMemoComparison);

ChatWidget.displayName = 'ChatWidget';

export default ChatWidget;